import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Singleton compartilhado: independentemente de quantos componentes
 * usem o hook (FloatingChatButton + Layout + Home = 3-4 chamadas em
 * paralelo), apenas UMA query é disparada a cada 30s, e todos os
 * subscribers recebem o mesmo valor.
 *
 * Pausa quando a aba está em background.
 */

type Listener = (
  n: number,
  perConv: Record<string, number>,
  manualUnread: Set<string>,
  mentionsPerConv: Record<string, number>,
) => void;

let currentUserId: string | null = null;
let currentTotal = 0;
let currentPerConv: Record<string, number> = {};
let currentManualUnread: Set<string> = new Set();
let currentMentionsPerConv: Record<string, number> = {};
let listeners = new Set<Listener>();
let intervalId: ReturnType<typeof setInterval> | null = null;
let visibilityBound = false;
let inflight = false;
let pending = false;
let realtimeChannel: ReturnType<typeof supabase.channel> | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let realtimeHealthy = false;
let fallbackIntervalId: ReturnType<typeof setInterval> | null = null;
let realtimeRetryTimer: ReturnType<typeof setTimeout> | null = null;

async function fetchUnread(userId: string) {
  if (inflight) { pending = true; return; }
  inflight = true;
  try {
    const [{ data }, { data: parts }, { data: mentions }] = await Promise.all([
      supabase.rpc('get_chat_unread_counts', { _user_id: userId }),
      supabase
        .from('chat_participants')
        .select('conversation_id, manually_unread')
        .eq('user_id', userId)
        .eq('manually_unread', true),
      // Menções não lidas, para o "@" da lista de conversas. Falhar aqui (função
      // ainda não aplicada no banco) só deixa o "@" de fora, sem afetar o resto.
      supabase.rpc('get_chat_unread_mentions'),
    ]);
    const perConv: Record<string, number> = {};
    let total = 0;
    if (Array.isArray(data)) {
      for (const row of data as any[]) {
        const n = Number(row.unread_count);
        perConv[row.conversation_id] = n;
        total += n;
      }
    }
    const manualUnread = new Set<string>();
    if (Array.isArray(parts)) {
      for (const p of parts as any[]) {
        manualUnread.add(p.conversation_id);
        // Só soma se essa conversa ainda não tinha contagem real — senão uma
        // conversa marcada manualmente E com mensagem nova contava em dobro.
        if (!perConv[p.conversation_id]) total += 1;
      }
    }
    const mentionsPerConv: Record<string, number> = {};
    if (Array.isArray(mentions)) {
      for (const row of mentions) mentionsPerConv[row.conversation_id] = Number(row.mention_count);
    }
    currentTotal = total;
    currentPerConv = perConv;
    currentManualUnread = manualUnread;
    currentMentionsPerConv = mentionsPerConv;
    listeners.forEach((l) => l(total, perConv, manualUnread, mentionsPerConv));
  } catch {
    // silencioso
  } finally {
    inflight = false;
    if (pending) {
      pending = false;
      if (currentUserId) void fetchUnread(currentUserId);
    }
  }
}

function scheduleFetch() {
  if (!currentUserId) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    if (currentUserId) void fetchUnread(currentUserId);
  }, 250);
}

/**
 * Pede a recontagem logo depois de marcar uma conversa como lida. O evento do
 * realtime faria o mesmo, mas ele se perde quando o canal ainda está
 * conectando — que é justamente o caso de quem acabou de abrir o chat. Passa
 * pelo mesmo debounce do evento, então os dois juntos viram uma busca só.
 */
export function atualizarNaoLidas() {
  scheduleFetch();
}

function startFastFallback() {
  if (fallbackIntervalId) return;
  // Enquanto realtime estiver instável, polling agressivo (5s)
  fallbackIntervalId = setInterval(() => {
    if (document.visibilityState === 'visible' && currentUserId) {
      void fetchUnread(currentUserId);
    }
  }, 5000);
}

function stopFastFallback() {
  if (fallbackIntervalId) {
    clearInterval(fallbackIntervalId);
    fallbackIntervalId = null;
  }
}

function scheduleRealtimeRetry(userId: string) {
  if (realtimeRetryTimer) return;
  realtimeRetryTimer = setTimeout(() => {
    realtimeRetryTimer = null;
    if (!currentUserId) return;
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      realtimeChannel = null;
    }
    ensureRealtime(userId);
  }, 3000);
}

function ensureRealtime(userId: string) {
  if (realtimeChannel) return;
  realtimeHealthy = false;
  startFastFallback(); // fallback ativo até canal confirmar SUBSCRIBED
  realtimeChannel = supabase
    .channel(`unread-badge-${userId}`)
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, () => scheduleFetch())
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_participants', filter: `user_id=eq.${userId}` }, () => scheduleFetch())
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_participants', filter: `user_id=eq.${userId}` }, () => scheduleFetch())
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        realtimeHealthy = true;
        stopFastFallback();
        // Resync ao (re)conectar — cobre eventos perdidos durante a queda
        void fetchUnread(userId);
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        realtimeHealthy = false;
        startFastFallback();
        scheduleRealtimeRetry(userId);
      }
    });
}

function teardownRealtime() {
  if (realtimeChannel) {
    supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }
  if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
  if (realtimeRetryTimer) { clearTimeout(realtimeRetryTimer); realtimeRetryTimer = null; }
  stopFastFallback();
  realtimeHealthy = false;
}

function ensurePolling() {
  if (intervalId || !currentUserId) return;
  // Backup polling padrão (60s) — fallback rápido (5s) cobre quedas do realtime
  intervalId = setInterval(() => {
    if (document.visibilityState === 'visible' && currentUserId) {
      void fetchUnread(currentUserId);
    }
  }, 60000);
  if (!visibilityBound) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && currentUserId) {
        void fetchUnread(currentUserId);
        if (!realtimeHealthy && currentUserId) {
          startFastFallback();
          scheduleRealtimeRetry(currentUserId);
        }
      }
    });
    window.addEventListener('focus', () => {
      if (currentUserId) void fetchUnread(currentUserId);
    });
    window.addEventListener('online', () => {
      if (currentUserId) {
        void fetchUnread(currentUserId);
        scheduleRealtimeRetry(currentUserId);
      }
    });
    visibilityBound = true;
  }
}

function stopPolling() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  teardownRealtime();
}



/**
 * Mesma fonte, mesmo timing do badge/contador — usado onde precisa saber
 * não lida POR conversa (ex.: lista de conversas do ChatContar). Compartilhar
 * o singleton com useUnreadMessages() é o que garante que o número que
 * aparece na barra lateral e o que aparece dentro do chat nunca se
 * desincronizem: os dois vêm da mesma busca, no mesmo instante.
 */
export function useUnreadPerConversation() {
  const { user } = useAuth();
  const [state, setState] = useState({
    total: currentTotal,
    perConv: currentPerConv,
    manualUnread: currentManualUnread,
    mentionsPerConv: currentMentionsPerConv,
  });

  useEffect(() => {
    if (!user) return;

    // Troca de usuário invalida o cache
    if (currentUserId !== user.id) {
      currentUserId = user.id;
      currentTotal = 0;
      currentPerConv = {};
      currentManualUnread = new Set();
      currentMentionsPerConv = {};
      void fetchUnread(user.id);
    }

    const listener: Listener = (total, perConv, manualUnread, mentionsPerConv) =>
      setState({ total, perConv, manualUnread, mentionsPerConv });
    listeners.add(listener);
    setState({
      total: currentTotal,
      perConv: currentPerConv,
      manualUnread: currentManualUnread,
      mentionsPerConv: currentMentionsPerConv,
    });
    ensurePolling();
    ensureRealtime(user.id);

    // Primeira chamada se ainda não houver valor
    if (currentTotal === 0) void fetchUnread(user.id);

    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        stopPolling();
        currentUserId = null;
      }
    };
  }, [user]);

  return state;
}

export function useUnreadMessages() {
  return useUnreadPerConversation().total;
}
